import { useEffect } from 'react';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import {
  ServicesSubSectionContainer,
  ServicesSubSectionHeader,
} from '../../services/services-subsection/services-subsection.styles';
import { Content, CustomHeader, MarginContainer } from './insurance-fee.styles';
import { useStore } from '../../../stores/store';
import FormFieldText from '../../../common/form-field-text/form-field-text.component';
import InsuranceTooltip from '../../../common/insurance-tooltip/insurance-tooltip.component';
import { InsuranceLimits } from '../../../models/insuranceLimits';
import { getFormattedPrice } from '../../../utils/price/get-formatted-price';
import {
  PriceValue,
  PriceTitle,
} from '../../../routes/parcel-content/parcel-content.styles';
import CustomSkeleton from '../../../common/skeletons/custom-skeleton-component';
import { FlexContainer } from '../../../common/flex-container/flex-container.styles';
import { getCountryTranslationKey } from '../../../utils/translations/translations.utils';

const calculateInsuranceFee = (amount?: number, limits?: InsuranceLimits) => {
  if (!limits || !limits?.max_free_value || !limits.coefficient || !amount)
    return 0;

  if (amount <= limits.max_free_value || amount > limits.max_insurance) {
    return 0;
  }

  return parseFloat((amount * limits.coefficient).toFixed(2));
};

const ChangeHandler = () => {
  const {
    parcelCreationStore: { domesticInsurance, prefillInProgress },
  } = useStore();

  const { setFieldValue, initialValues } = useFormikContext<{
    insuranceAmount: string | undefined;
  }>();

  // Required for draft editing case,
  // because domesticInsurance is set after form is rendered with init values
  useEffect(() => {
    if (
      !prefillInProgress &&
      !initialValues.insuranceAmount &&
      domesticInsurance
    ) {
      setFieldValue('insuranceAmount', domesticInsurance);
    }
  }, [prefillInProgress, domesticInsurance, initialValues, setFieldValue]);

  return undefined;
};

const InsuranceFeeDomestic = () => {
  const {
    userStore: { getCurrencySymbol },
    parcelCreationStore: {
      fetchDomesticInsuranceLimits,
      insuranceLimits,
      setDomesticInsurance,
      domesticInsurance,
      resetShipmentDataInCaseDeliveryDetailsChanged,
      isNextButtonClicked,
      prefillInProgress,
      isLoadingShipmentApi,
      countryDeparture,
      countryDestination,
    },
  } = useStore();

  useEffect(() => {
    fetchDomesticInsuranceLimits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryDeparture?.code, countryDestination?.code]);

  useEffect(() => {
    // TEMP fix. Ignore default insurance and leave it undefined.
    if (!domesticInsurance && insuranceLimits?.default_value) {
      // setDomesticInsurance(insuranceLimits.default_value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceLimits, setDomesticInsurance]);

  const { t } = useTranslation();

  const currencyCode = insuranceLimits?.currency;

  const getInsuranceFeeElement = (amount: number | undefined, fee: number) => {
    if (!insuranceLimits) return null;
    const showSkeleton = isLoadingShipmentApi || prefillInProgress;

    return (
      <FlexContainer
        $justify='start'
        $gap='1rem'>
        <PriceTitle>
          {t(getCountryTranslationKey('insurance_fee', countryDeparture?.code))}
          :
        </PriceTitle>
        {showSkeleton ? (
          <CustomSkeleton
            width='12rem'
            height='1.8rem'
          />
        ) : (
          <PriceValue
            $free={!amount || amount <= insuranceLimits.min_insurance || !fee}>
            {fee > 0 && amount && amount > insuranceLimits.min_insurance
              ? `${getFormattedPrice(fee, getCurrencySymbol(currencyCode))}`
              : t('free')}
          </PriceValue>
        )}
      </FlexContainer>
    );
  };

  const insuranceFee = calculateInsuranceFee(
    domesticInsurance,
    insuranceLimits
  );

  const validationSchema = Yup.object({
    insuranceAmount: Yup.number()
      // .transform is required to not show NaN error for undefined value in the input
      .transform((value, originalValue) => {
        if (
          originalValue === undefined ||
          originalValue === null ||
          originalValue === ''
        ) {
          return undefined;
        }

        return Number.isNaN(value) ? undefined : value;
      })
      .max(
        insuranceLimits?.max_insurance ?? 0,
        t('max_declared_value_amount', {
          maxDeclaredValueAmount: getFormattedPrice(
            insuranceLimits?.max_insurance,
            getCurrencySymbol(currencyCode)
          ),
        })
      )
      .min(
        insuranceLimits?.min_insurance ?? 0,
        t('min_declared_value_amount', {
          minDeclaredValueAmount: getFormattedPrice(
            insuranceLimits?.min_insurance,
            getCurrencySymbol(currencyCode)
          ),
        })
      )
      .required(
        t('min_declared_value_amount', {
          minDeclaredValueAmount: getFormattedPrice(
            insuranceLimits?.min_insurance,
            getCurrencySymbol(currencyCode)
          ),
        })
      ),
  });

  const insuranceTooltip = t('insurance_tip_domestic', {
    freeInsuranceLimit: getFormattedPrice(
      insuranceLimits?.max_free_value,
      getCurrencySymbol(insuranceLimits?.currency)
    ),
    insuranceRate: (insuranceLimits?.coefficient ?? 0) * 100,
  });

  return (
    <ServicesSubSectionContainer>
      <ServicesSubSectionHeader $isEditable>
        <CustomHeader>
          {t(getCountryTranslationKey('insurance', countryDeparture?.code))}
          <InsuranceTooltip tooltipText={insuranceTooltip} />
        </CustomHeader>
      </ServicesSubSectionHeader>

      <Content>
        <Formik
          validationSchema={validationSchema}
          initialValues={{
            insuranceAmount: domesticInsurance?.toString(),
          }}
          onSubmit={() => {}}
          validateOnChange>
          {() => (
            <form>
              <MarginContainer
                $mbTablet='1.6rem'
                $mb='2rem'>
                <FormFieldText
                  validationMethod='on-type'
                  showSkeleton={prefillInProgress}
                  triggerValidation={isNextButtonClicked.value}
                  label='declared_value'
                  name='insuranceAmount'
                  disabled={isLoadingShipmentApi || !insuranceLimits}
                  infoText={getCurrencySymbol(currencyCode)}
                  inputMode='decimal'
                  onChange={(event) => {
                    const parsedInsurance = event.target.value
                      ? Number.parseFloat(event.target.value)
                      : undefined;
                    const newInsurance = Number.isNaN(parsedInsurance)
                      ? undefined
                      : parsedInsurance;
                    setDomesticInsurance(newInsurance);
                    resetShipmentDataInCaseDeliveryDetailsChanged();
                  }}
                />
              </MarginContainer>
              {getInsuranceFeeElement(domesticInsurance, insuranceFee)}
              <ChangeHandler />
            </form>
          )}
        </Formik>
      </Content>
    </ServicesSubSectionContainer>
  );
};

export default observer(InsuranceFeeDomestic);
